import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { Estudiante, FormEstudianteData } from 'models/estudiante_model';
import {
  getEstudiantes,
  getEstudiantesConInscripciones,
  createEstudiante,
  editEstudiante,
  deleteEstudiante,
  changeCursoEstudiante,
  type EstudianteFormData,
} from 'repositories/estudiante_repository';

interface EstudianteState {
  estudiantes: Estudiante[];
  estudiante: Estudiante | null;
  formEstudiante: FormEstudianteData;
  loading: boolean;
  error: string | null;
}

const initialState: EstudianteState = {
  estudiantes: [],
  estudiante: null,
  formEstudiante: {},
  loading: false,
  error: null,
};

export const cargarEstudiantes = createAsyncThunk<Estudiante[], { cursoFilter?: string; turnoFilter?: string; nombreFilter?: string } | undefined, { rejectValue: string }>(
  'estudiante/cargarEstudiantes',
  async (
    filters = {},
    { rejectWithValue },
  ) => {
    try {
      return (
        (await getEstudiantes(filters.cursoFilter, filters.turnoFilter, filters.nombreFilter)) ?? []
      );
    } catch {
      return rejectWithValue('Error al cargar estudiantes');
    }
  },
);

export const cargarEstudiantesConInscripciones = createAsyncThunk<Estudiante[], { cursoFilter?: string; turnoFilter?: string; nombreFilter?: string; gestionFilter?: string } | undefined, { rejectValue: string }>(
  'estudiante/cargarEstudiantesConInscripciones',
  async (
    filters = {},
    { rejectWithValue },
  ) => {
    try {
      return (
        (await getEstudiantesConInscripciones(
          filters.cursoFilter,
          filters.turnoFilter,
          filters.nombreFilter,
          filters.gestionFilter,
        )) ?? []
      );
    } catch {
      return rejectWithValue('Error al cargar estudiantes con inscripciones');
    }
  },
);

export const agregarEstudiante = createAsyncThunk(
  'estudiante/agregarEstudiante',
  async ({ data, file }: { data: EstudianteFormData; file?: File | File[] | FileList }, { rejectWithValue }) => {
    try {
      await createEstudiante(data, file);
    } catch {
      return rejectWithValue('Error al crear estudiante');
    }
  },
);

export const editarEstudiante = createAsyncThunk(
  'estudiante/editarEstudiante',
  async ({ data, estudianteId, file }: { data: EstudianteFormData; estudianteId: string; file?: File | File[] | FileList }, { rejectWithValue }) => {
    try {
      await editEstudiante(data, estudianteId, file);
    } catch {
      return rejectWithValue('Error al editar estudiante');
    }
  },
);

export const eliminarEstudiante = createAsyncThunk(
  'estudiante/eliminarEstudiante',
  async ({ estudianteId, cursoId }: { estudianteId: string; cursoId: string }, { rejectWithValue }) => {
    try {
      await deleteEstudiante(estudianteId, cursoId);
    } catch {
      return rejectWithValue('Error al eliminar estudiante');
    }
  },
);

export const cambiarCursoEstudiante = createAsyncThunk(
  'estudiante/cambiarCursoEstudiante',
  async (
    { estudianteId, cursoId, nuevoCurso }: { estudianteId: string; cursoId: string; nuevoCurso: string },
    { rejectWithValue },
  ) => {
    try {
      await changeCursoEstudiante(estudianteId, cursoId, nuevoCurso);
    } catch {
      return rejectWithValue('Error al cambiar curso del estudiante');
    }
  },
);

export const estudianteSlice = createSlice({
  name: 'estudiante',
  initialState,
  reducers: {
    changeEstudiante: (state, action: PayloadAction<Estudiante | null>) => {
      state.estudiante = action.payload;
    },
    setFormEstudiante: (state, action: PayloadAction<FormEstudianteData>) => {
      state.formEstudiante = action.payload;
    },
    setEstudiantes: (state, action: PayloadAction<Estudiante[]>) => {
      state.estudiantes = action.payload;
    },
  },
  extraReducers: (builder) => {
    const setEstudiantesReducer = (state: EstudianteState, action: { payload: Estudiante[] }) => {
      state.loading = false;
      state.estudiantes = action.payload;
    };
    builder
      .addCase(cargarEstudiantes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarEstudiantes.fulfilled, setEstudiantesReducer)
      .addCase(cargarEstudiantes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(cargarEstudiantesConInscripciones.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarEstudiantesConInscripciones.fulfilled, setEstudiantesReducer)
      .addCase(cargarEstudiantesConInscripciones.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { changeEstudiante, setFormEstudiante, setEstudiantes } = estudianteSlice.actions;
export default estudianteSlice.reducer;
